import { useState } from "react";
import { api, SCHEDULE_C_CATEGORIES } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Trash2, Pencil, Check, Receipt } from "lucide-react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

export default function ReceiptsTable({ receipts, onChanged }) {
  const [editId, setEditId] = useState(null);
  const [draft, setDraft] = useState({});

  const startEdit = (r) => {
    setEditId(r.id);
    setDraft({ vendor: r.vendor, amount: r.amount, date: r.date, category: r.category, notes: r.notes });
  };

  const saveEdit = async (id) => {
    try {
      await api.put(`/receipts/${id}`, { ...draft, amount: parseFloat(draft.amount || 0) });
      setEditId(null);
      onChanged();
      toast.success("Updated");
    } catch (e) {
      toast.error("Update failed");
    }
  };

  const del = async (id) => {
    try {
      await api.delete(`/receipts/${id}`);
      onChanged();
      toast.success("Deleted");
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  if (!receipts.length) {
    return (
      <div data-testid="empty-receipts" className="rounded-2xl border border-border p-12 text-center">
        <Receipt className="h-10 w-10 mx-auto text-muted-foreground/40 mb-3" />
        <p className="font-heading font-semibold text-lg">No receipts yet</p>
        <p className="text-sm text-muted-foreground">Snap your first receipt above to get started.</p>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-border overflow-hidden bg-card">
      <Table data-testid="receipts-table">
        <TableHeader>
          <TableRow className="bg-secondary/50 hover:bg-secondary/50">
            <TableHead className="font-semibold">Date</TableHead>
            <TableHead className="font-semibold">Vendor</TableHead>
            <TableHead className="font-semibold">Category</TableHead>
            <TableHead className="font-semibold text-right">Amount</TableHead>
            <TableHead className="w-24 text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {receipts.map((r) => (
            <TableRow key={r.id} data-testid={`receipt-row-${r.id}`} className="border-b border-border/60 hover:bg-secondary/40 transition-colors">
              {editId === r.id ? (
                <>
                  <TableCell>
                    <Input type="date" value={draft.date} onChange={(e) => setDraft({ ...draft, date: e.target.value })} className="h-9 rounded-lg" />
                  </TableCell>
                  <TableCell>
                    <Input value={draft.vendor} onChange={(e) => setDraft({ ...draft, vendor: e.target.value })} className="h-9 rounded-lg" />
                  </TableCell>
                  <TableCell>
                    <Select value={draft.category} onValueChange={(v) => setDraft({ ...draft, category: v })}>
                      <SelectTrigger data-testid={`edit-category-${r.id}`} className="h-9 rounded-lg"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {SCHEDULE_C_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="text-right">
                    <Input type="number" step="0.01" value={draft.amount} onChange={(e) => setDraft({ ...draft, amount: e.target.value })} className="h-9 rounded-lg text-right" />
                  </TableCell>
                  <TableCell className="text-right">
                    <Button data-testid={`save-edit-${r.id}`} size="icon" variant="ghost" onClick={() => saveEdit(r.id)} className="h-8 w-8 text-green-600">
                      <Check className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </>
              ) : (
                <>
                  <TableCell className="text-muted-foreground">{r.date}</TableCell>
                  <TableCell className="font-medium">{r.vendor}</TableCell>
                  <TableCell>
                    <span className="inline-flex text-xs font-semibold px-2.5 py-1 rounded-full bg-primary/10 text-primary border border-primary/20">{r.category}</span>
                  </TableCell>
                  <TableCell className="text-right font-heading font-bold tabular-nums">${Number(r.amount).toFixed(2)}</TableCell>
                  <TableCell className="text-right">
                    <Button data-testid={`edit-receipt-${r.id}`} size="icon" variant="ghost" onClick={() => startEdit(r)} className="h-8 w-8">
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button data-testid={`delete-receipt-${r.id}`} size="icon" variant="ghost" onClick={() => del(r.id)} className="h-8 w-8 text-destructive">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
